import { Component, Inject, ChangeDetectorRef, ViewChild, AfterViewInit } from '@angular/core';

import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { ExtendedLogsTableComponent } from '../../admin/extended-logs/extended-logs-table/extended-logs-table.component';
import { AdminInteraction } from '../../../services/interactions.service';
import { SmallInputMenuComponent } from '../../../shared/components/small-input-menu/small-input-menu.component';
import { SmallIconMenuComponent, SmallIconMenuAction } from '../../../shared/components/small-icon-menu/small-icon-menu.component';
import { CallCenterChipComponent, CallCenterChipConfig } from '../../../shared/components/call-center-chip/call-center-chip.component';
import { CircleIconButtonComponent } from '../../../shared/components/circle-icon-button/circle-icon-button.component';
import { InputDialogData, InputDialogResult, DuplicateEntityInfo } from '../../../interfaces/input-dialog.interface';
import { ReminderDialogComponent, ReminderDialogResult } from './reminder-dialog/reminder-dialog.component';
import { UploadService } from '../../../services/upload.service';

type ChipStatus = 'default' | 'confirmed' | 'rejected';
type ContactType = 'official' | 'email' | 'phone';

interface ValidStatusUpdate {
  validStatus: boolean | null;
  contactType: ContactType;
  isNewContact?: boolean;  // Új mező
}

@Component({
  selector: 'app-input-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatIconModule,
    MatChipsModule,
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    ExtendedLogsTableComponent,
    SmallInputMenuComponent,
    SmallIconMenuComponent,
    CallCenterChipComponent,
    CircleIconButtonComponent
],
  templateUrl: './input-dialog.component.html',
  styleUrls: ['./input-dialog.component.scss'],
  host: {
    // Show wide layout when there are logs OR substantial contact information
    // This prevents content squishing when logs fail to load (e.g., no internet)
    '[class.has-logs]': '(data.showLogs && data.logs && data.logs.length > 0) || hasSubstantialContactInfo()'
  }
})
export class InputDialogComponent implements AfterViewInit {
  @ViewChild('addPhoneMenuTrigger') addPhoneMenuTrigger!: MatMenuTrigger;
  @ViewChild('addEmailMenuTrigger') addEmailMenuTrigger!: MatMenuTrigger;
  @ViewChild('addOfficialMenuTrigger') addOfficialMenuTrigger!: MatMenuTrigger;
  @ViewChild('smallInputMenuComponent') smallInputMenuComponent!: SmallInputMenuComponent;
  @ViewChild('smallEmailInputMenuComponent') smallEmailInputMenuComponent!: SmallInputMenuComponent;
  @ViewChild('smallOfficialInputMenuComponent') smallOfficialInputMenuComponent!: SmallInputMenuComponent;
  
  // Form controls
  readonly inputControl = new FormControl('');
  selectedStatus = '';
  selectedItem = '';
  currentMenuTrigger: MatMenuTrigger | null = null;
  
  // Call quality checkboxes
  targetPersonReached = false;
  listenedFully = false;

  // Visszahívási emlékeztető
  reminderDate: Date | null = null;
  reminderTimeISO: string | undefined = undefined;
  showReminderSection = false;

  // Ismétlődő telefonszám jelzés
  hasDuplicatePhones = false;
  duplicatePhonesList: { phone: string; entities: DuplicateEntityInfo[] }[] = [];
  duplicatePhonesSet = new Set<string>();
  
  // Form state
  newOfficialName = '';
  editingOfficialName: string | null = null; // Ha nem null, akkor szerkesztés folyik
  editingPhoneValue: string | null = null; // Telefonszám szerkesztési mód
  editingEmailValue: string | null = null; // Email szerkesztési mód
  editingPhoneInput = ''; // Input mező értéke telefonszám szerkesztésnél
  editingEmailInput = ''; // Input mező értéke email szerkesztésnél
  
  // Track edited officials: oldName -> newName
  private readonly editedOfficials = new Map<string, string>();
  // Track edited phones: oldValue -> newValue
  private readonly editedPhones = new Map<string, string>();
  // Track edited emails: oldValue -> newValue
  private readonly editedEmails = new Map<string, string>();
  
  // Initial state tracking
  private readonly initialState = {
    officials: [] as string[],
    phones: [] as string[],
    emails: [] as string[]
  };
  
  // Current display state
  displayEmails: string[] = [];
  
  // Status tracking
  private readonly chipStatuses = new Map<string, ChipStatus>();

  // Current user for edit permissions
  get currentUser() {
    return this.data.currentUser;
  }
  
  get isAdmin(): boolean {
    return this.currentUser?.jobs?.includes('admin') ?? false;
  }

  /**
   * Ellenőrzi, hogy van-e érdemi kontakt információ
   * Ez biztosítja a széles layout megjelenítését akkor is, ha a log-ok nem töltődnek be
   */
  hasSubstantialContactInfo(): boolean {
    return !!(
      (this.data.officials && this.data.officials.length > 0) ||
      (this.data.phoneNumbers && this.data.phoneNumbers.length > 0) ||
      (this.displayEmails && this.displayEmails.length > 0) ||
      (this.data.emails && this.data.emails.length > 0)
    );
  }

  constructor(
    public dialogRef: MatDialogRef<InputDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InputDialogData,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private readonly uploadService: UploadService
  ) {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    // Initialize form controls
    this.inputControl.setValue(this.data.initialValue || '');
    this.selectedStatus = ''; // Force user to select explicitly

    // Store initial state
    this.initialState.officials = [...(this.data.officials || [])];
    this.initialState.phones = [...(this.data.phoneNumbers || [])];
    this.initializeEmails();
    // Ne inicializáljuk a chipStatuses Map-et - üres Map marad
    // Ha nincs benne a chip, az jelenti a "nincs interakció" állapotot
  }

  private initializeEmails(): void {
    // Support multiple emails with backward compatibility
    if (this.data.emails?.length) {
      this.initialState.emails = [...this.data.emails];
      this.displayEmails = [...this.data.emails];
    } else if (this.data.email && this.data.email !== 'Nincs email') {
      // Parse comma-separated emails (backward compatibility)
      const emails = this.data.email.split(',')
        .map(email => email.trim())
        .filter(email => email.length > 0);
      this.initialState.emails = emails;
      this.displayEmails = [...emails];
    } else {
      this.initialState.emails = [];
      this.displayEmails = [];
    }
  }

  ngAfterViewInit(): void {
    // No need to modify data.emails anymore - we use displayEmails
    this.cdr.detectChanges();
    this.checkDuplicatePhones();
  }

  private checkDuplicatePhones(): void {
    if (!this.data.phoneNumbers?.length || !this.data.companyId) return;

    this.uploadService.checkDuplicatePhones(this.data.phoneNumbers, this.data.companyId)
      .subscribe({
        next: (response) => {
          const hasResults = Object.keys(response.duplicates).length > 0;
          this.hasDuplicatePhones = hasResults;
          if (hasResults) {
            this.duplicatePhonesList = Object.entries(response.duplicates).map(
              ([phone, entities]) => ({ phone, entities })
            );
            this.duplicatePhonesSet = new Set(Object.keys(response.duplicates));
          } else {
            this.duplicatePhonesSet.clear();
          }
          this.cdr.detectChanges();
        },
        error: () => {
          // Hiba esetén nem jelenítünk meg semmit
        }
      });
  }

  private static readonly CC_STATUS_LABELS: { [key: string]: string } = {
    'hivando': 'Hívandó',
    'visszahivando': 'Visszahívandó',
    'elutasitott': 'Elutasított',
    'robinson_listas': 'Robinson listás',
    'nem_vette_fel': 'Nem vette fel',
    'egyeztetett-kum': 'Egyeztetett (KÜM)',
    'egyeztetett-vagyon': 'Egyeztetett (vagyon)',
    'egyeztetett-kv': 'Egyeztetett (K+v)'
  };

  getCcStatusLabel(status: string | null): string {
    if (!status) return 'Hívandó';
    return InputDialogComponent.CC_STATUS_LABELS[status] || status;
  }

  onCancel(): void {
    this.dialogRef.close({
      confirmed: false,
      value: ''
    } as InputDialogResult);
  }

  onSave(): void {
    if (!this.isValid()) {
      return;
    }
    
    // Collect new contacts that were added during the dialog
    const currentOfficials = this.data.officials || [];
    const currentPhones = this.data.phoneNumbers || [];
    const currentEmails = this.displayEmails || [];
    
    // Get edited official new names to exclude them from newOfficials
    const editedOfficialNewNames = new Set(Array.from(this.editedOfficials.values()));
    const editedPhoneNewValues = new Set(Array.from(this.editedPhones.values()));
    const editedEmailNewValues = new Set(Array.from(this.editedEmails.values()));
    
    // Filter out: 1) contacts that were in initialState, 2) contacts that are result of editing
    const newOfficials = currentOfficials.filter(o => 
      !this.initialState.officials.includes(o) && !editedOfficialNewNames.has(o)
    );
    const newPhones = currentPhones.filter(p => 
      !this.initialState.phones.includes(p) && !editedPhoneNewValues.has(p)
    );
    const newEmails = currentEmails.filter(e => 
      !this.initialState.emails.includes(e) && !editedEmailNewValues.has(e)
    );
    
    // Collect validstatus changes for existing and new contacts
    const validStatusUpdates: { [contactValue: string]: ValidStatusUpdate } = {};
    
    // Process chip status changes for all contacts
    // Ha a chipStatuses Map-ben van a chip, az azt jelenti, hogy a felhasználó interakcióba lépett vele
    // Ilyenkor MINDIG vissza kell küldeni a validstatus változást (akár null, true, vagy false)
    this.chipStatuses.forEach((chipStatus, contactValue) => {
      const contactType = this.determineContactType(contactValue, currentOfficials, currentPhones, currentEmails);
      if (!contactType) return;
      
      const validStatus = this.convertChipStatusToValidStatus(chipStatus);
      
      // Ellenőrizzük, hogy ez új kontakt-e
      const isNewContact = newOfficials.includes(contactValue) || 
                          newPhones.includes(contactValue) || 
                          newEmails.includes(contactValue);
      
      // Mindig visszaküldjük, ha van bejegyzés a Map-ben (felhasználói interakció történt)
      validStatusUpdates[contactValue] = { 
        validStatus, 
        contactType,
        isNewContact  // Új flag: jelöljük, hogy ez új kontakt-e
      };
    });
    
    // Convert editedOfficials Map to object
    const editedOfficialsObj: { [oldName: string]: string } = {};
    this.editedOfficials.forEach((newName, oldName) => {
      editedOfficialsObj[oldName] = newName;
    });
    
    // Convert editedPhones Map to object
    const editedPhonesObj: { [oldValue: string]: string } = {};
    this.editedPhones.forEach((newValue, oldValue) => {
      editedPhonesObj[oldValue] = newValue;
    });
    
    // Convert editedEmails Map to object
    const editedEmailsObj: { [oldValue: string]: string } = {};
    this.editedEmails.forEach((newValue, oldValue) => {
      editedEmailsObj[oldValue] = newValue;
    });
    
    this.dialogRef.close({
      confirmed: true,
      value: this.inputControl.value?.trim() || '',
      selectedStatus: this.selectedStatus,
      assetInsurance: this.selectedStatus === 'egyeztetett-v',
      targetPersonReached: this.targetPersonReached,
      listenedFully: this.listenedFully,
      newOfficials: newOfficials.length > 0 ? newOfficials : undefined,
      editedOfficials: this.editedOfficials.size > 0 ? editedOfficialsObj : undefined,
      editedPhones: this.editedPhones.size > 0 ? editedPhonesObj : undefined,
      editedEmails: this.editedEmails.size > 0 ? editedEmailsObj : undefined,
      newPhones: newPhones.length > 0 ? newPhones : undefined,
      newEmails: newEmails.length > 0 ? newEmails : undefined,
      validStatusUpdates: Object.keys(validStatusUpdates).length > 0 ? validStatusUpdates : undefined,
      reminderTime: this.getReminderDateTime()
    } as InputDialogResult);
  }

  private determineContactType(
    contactValue: string, 
    officials: string[], 
    phones: string[], 
    emails: string[]
  ): ContactType | null {
    if (officials.includes(contactValue)) return 'official';
    if (phones.includes(contactValue)) return 'phone';
    if (emails.includes(contactValue) || contactValue === this.data.email) return 'email';
    return null;
  }

  private convertChipStatusToValidStatus(chipStatus: ChipStatus): boolean | null {
    switch (chipStatus) {
      case 'confirmed': return true;
      case 'rejected': return false;
      case 'default': return null;
    }
  }

  isValid(): boolean {
    // Check if input is required and filled
    if (this.data.required && !this.inputControl.value?.trim()) {
      return false;
    }
    
    // Check if status selector is shown and status is required (must be selected)
    if (this.data.showStatusSelector && !this.selectedStatus) {
      return false;
    }
    
    return true;
  }

  /**
   * Ellenőrzi, hogy történt-e bármilyen módosítás a dialog megnyitása óta.
   * Módosításnak számít: új kontakt, szerkesztett kontakt, chip státusz változás, megjegyzés írása.
   */
  hasUnsavedChanges(): boolean {
    const currentOfficials = this.data.officials || [];
    const currentPhones = this.data.phoneNumbers || [];
    const currentEmails = this.displayEmails || [];

    // Új kontaktok hozzáadva
    const editedOfficialNewNames = new Set(Array.from(this.editedOfficials.values()));
    const editedPhoneNewValues = new Set(Array.from(this.editedPhones.values()));
    const editedEmailNewValues = new Set(Array.from(this.editedEmails.values()));

    const hasNewOfficials = currentOfficials.some(o =>
      !this.initialState.officials.includes(o) && !editedOfficialNewNames.has(o)
    );
    const hasNewPhones = currentPhones.some(p =>
      !this.initialState.phones.includes(p) && !editedPhoneNewValues.has(p)
    );
    const hasNewEmails = currentEmails.some(e =>
      !this.initialState.emails.includes(e) && !editedEmailNewValues.has(e)
    );

    // Szerkesztett kontaktok
    const hasEditedOfficials = this.editedOfficials.size > 0;
    const hasEditedPhones = this.editedPhones.size > 0;
    const hasEditedEmails = this.editedEmails.size > 0;

    // Chip valid státusz változások
    const hasChipStatusChanges = this.chipStatuses.size > 0;

    // Megjegyzés írása
    const hasNotes = !!(this.inputControl.value?.trim());

    return hasNewOfficials || hasNewPhones || hasNewEmails ||
           hasEditedOfficials || hasEditedPhones || hasEditedEmails ||
           hasChipStatusChanges || hasNotes;
  }

  /**
   * "Módosítások mentése" - Adatok mentése státuszváltozás nélkül.
   * Ugyanazt az eredményt adja vissza mint onSave(), de selectedStatus üres
   * és saveChangesOnly=true flag-gel.
   */
  onSaveChangesOnly(): void {
    if (!this.hasUnsavedChanges()) {
      return;
    }

    // Collect new contacts that were added during the dialog
    const currentOfficials = this.data.officials || [];
    const currentPhones = this.data.phoneNumbers || [];
    const currentEmails = this.displayEmails || [];

    // Get edited contact new names to exclude from new contacts
    const editedOfficialNewNames = new Set(Array.from(this.editedOfficials.values()));
    const editedPhoneNewValues = new Set(Array.from(this.editedPhones.values()));
    const editedEmailNewValues = new Set(Array.from(this.editedEmails.values()));

    const newOfficials = currentOfficials.filter(o =>
      !this.initialState.officials.includes(o) && !editedOfficialNewNames.has(o)
    );
    const newPhones = currentPhones.filter(p =>
      !this.initialState.phones.includes(p) && !editedPhoneNewValues.has(p)
    );
    const newEmails = currentEmails.filter(e =>
      !this.initialState.emails.includes(e) && !editedEmailNewValues.has(e)
    );

    // Collect validstatus changes
    const validStatusUpdates: { [contactValue: string]: ValidStatusUpdate } = {};
    this.chipStatuses.forEach((chipStatus, contactValue) => {
      const contactType = this.determineContactType(contactValue, currentOfficials, currentPhones, currentEmails);
      if (!contactType) return;

      const validStatus = this.convertChipStatusToValidStatus(chipStatus);
      const isNewContact = newOfficials.includes(contactValue) ||
                          newPhones.includes(contactValue) ||
                          newEmails.includes(contactValue);

      validStatusUpdates[contactValue] = {
        validStatus,
        contactType,
        isNewContact
      };
    });

    // Convert edited Maps to objects
    const editedOfficialsObj: { [oldName: string]: string } = {};
    this.editedOfficials.forEach((newName, oldName) => {
      editedOfficialsObj[oldName] = newName;
    });

    const editedPhonesObj: { [oldValue: string]: string } = {};
    this.editedPhones.forEach((newValue, oldValue) => {
      editedPhonesObj[oldValue] = newValue;
    });

    const editedEmailsObj: { [oldValue: string]: string } = {};
    this.editedEmails.forEach((newValue, oldValue) => {
      editedEmailsObj[oldValue] = newValue;
    });

    this.dialogRef.close({
      confirmed: true,
      saveChangesOnly: true,
      value: this.inputControl.value?.trim() || '',
      selectedStatus: '', // Nincs státuszváltozás
      assetInsurance: false,
      targetPersonReached: false,
      listenedFully: false,
      newOfficials: newOfficials.length > 0 ? newOfficials : undefined,
      editedOfficials: this.editedOfficials.size > 0 ? editedOfficialsObj : undefined,
      editedPhones: this.editedPhones.size > 0 ? editedPhonesObj : undefined,
      editedEmails: this.editedEmails.size > 0 ? editedEmailsObj : undefined,
      newPhones: newPhones.length > 0 ? newPhones : undefined,
      newEmails: newEmails.length > 0 ? newEmails : undefined,
      validStatusUpdates: Object.keys(validStatusUpdates).length > 0 ? validStatusUpdates : undefined
    } as InputDialogResult);
  }

  // Get chip status
  getChipStatus(chipValue: string): ChipStatus {
    return this.chipStatuses.get(chipValue) || 'default';
  }

  // Get current effective status of a chip (considering both backend validstatus and user interactions)
  getCurrentEffectiveStatus(chipValue: string): 'confirmed' | 'rejected' | 'default' {
    // First check if user has interacted with this chip
    if (this.chipStatuses.has(chipValue)) {
      const userStatus = this.chipStatuses.get(chipValue);
      if (userStatus === 'confirmed') {
        return 'confirmed';
      } else if (userStatus === 'rejected') {
        return 'rejected';
      } else if (userStatus === 'default') {
        return 'default'; // Explicit semleges (felhasználó által beállítva)
      }
    }
    
    // Ha nincs felhasználói interakció, backend validstatus-t használjuk
    let validStatus: boolean | null = null;
    
    // Check if it's an official
    if (this.data.officials?.includes(chipValue)) {
      validStatus = this.data.officialValidStatuses?.[chipValue] ?? null;
    }
    // Check if it's a phone number
    else if (this.data.phoneNumbers?.includes(chipValue)) {
      validStatus = this.data.phoneValidStatuses?.[chipValue] ?? null;
    }
    // Check if it's an email
    else if (chipValue === this.data.email) {
      validStatus = this.data.emailValidStatus ?? null;
    }
    
    // Determine effective status based on validstatus
    if (validStatus === true) {
      return 'confirmed';
    } else if (validStatus === false) {
      return 'rejected';
    } else {
      return 'default';
    }
  }

  // Helper method to convert chip data to CallCenterChipConfig format
  createChipConfig(text: string, type: 'official' | 'phone' | 'email' | 'status'): CallCenterChipConfig {
    let validStatus: boolean | null = null;
    
    // Use the same logic as getChipClass to determine validStatus
    if (this.chipStatuses.has(text)) {
      const userStatus = this.chipStatuses.get(text);
      if (userStatus === 'confirmed') {
        validStatus = true;
      } else if (userStatus === 'rejected') {
        validStatus = false;
      } else if (userStatus === 'default') {
        validStatus = null; // Explicit semleges
      }
    } else {
      // Check backend data
      switch (type) {
        case 'official':
          validStatus = this.data.officialValidStatuses?.[text] ?? null;
          break;
        case 'phone':
          validStatus = this.data.phoneValidStatuses?.[text] ?? null;
          break;
        case 'email':
          if (this.data.emailValidStatuses?.[text] !== undefined) {
            validStatus = this.data.emailValidStatuses[text];
          } else if (text === this.data.email) {
            validStatus = this.data.emailValidStatus ?? null;
          }
          break;
        case 'status':
          // For status chips, use validStatus to show selection (true = selected/green)
          const statusOption = this.data.statusOptions?.find(s => s.label === text);
          validStatus = statusOption && this.selectedStatus === statusOption.value ? true : null;
          break;
      }
    }
    
    return {
      text: text,
      type: type,
      validStatus: validStatus
    };
  }

  // Chip click handler that preserves original behavior
  onChipClick(chipConfig: CallCenterChipConfig, menuTrigger?: any) {
    if (chipConfig.type === 'status') {
      // For status chips, find the original status value and select it
      const statusOption = this.data.statusOptions?.find(s => s.label === chipConfig.text);
      if (statusOption) {
        this.selectStatus(statusOption.value);
      }
    } else {
      // For contact chips, use existing setSelectedItem logic
      this.setSelectedItem(chipConfig.text, menuTrigger);
    }
  }

  // Get chip class based on validstatus or chip status
  getChipClass(chipValue: string): string {
    // First check if user has interacted with this chip (Map.has() returns true only if key exists)
    if (this.chipStatuses.has(chipValue)) {
      const userStatus = this.chipStatuses.get(chipValue);
      if (userStatus === 'confirmed') {
        return 'chip-confirmed'; // Zöld - felhasználó megerősítette
      } else if (userStatus === 'rejected') {
        return 'chip-rejected'; // Piros - felhasználó elutasította
      } else if (userStatus === 'default') {
        return 'chip-neutral'; // Szürke - felhasználó semlegesre állította (toggle)
      }
    }
    
    // Ha nincs felhasználói interakció (nincs a Map-ben), backend validstatus-t használjuk
    let validStatus: boolean | null = null;
    
    // Check if it's an official
    if (this.data.officials?.includes(chipValue)) {
      validStatus = this.data.officialValidStatuses?.[chipValue] ?? null;
    }
    // Check if it's a phone number
    else if (this.data.phoneNumbers?.includes(chipValue)) {
      validStatus = this.data.phoneValidStatuses?.[chipValue] ?? null;
    }
    // Check if it's an email (multiple emails support)
    else if (this.data.emails?.includes(chipValue)) {
      validStatus = this.data.emailValidStatuses?.[chipValue] ?? null;
    }
    // Check if it's a single email (backward compatibility)
    else if (chipValue === this.data.email) {
      validStatus = this.data.emailValidStatus ?? null;
    }
    
    // Determine class based on validstatus
    if (validStatus === true) {
      return 'chip-valid'; // Zöld - backend szerint valid
    } else if (validStatus === false) {
      return 'chip-invalid'; // Piros - backend szerint invalid
    } else {
      return 'chip-neutral'; // Szürke - nincs backend adat
    }
  }

  // Track by function for ngFor
  trackByIndex(index: number, item: any): any {
    return item; // Track by item value instead of index
  }

  // --- Visszahívási emlékeztető metódusok ---

  openReminderDialog(): void {
    const ref = this.dialog.open(ReminderDialogComponent, {
      width: '590px',
      panelClass: 'reminder-dialog-panel',
    });

    ref.afterClosed().subscribe((result: ReminderDialogResult | null) => {
      if (result) {
        if (result.cleared) {
          this.reminderDate = null;
          this.reminderTimeISO = undefined;
        } else {
          this.reminderDate = result.reminderDate;
          this.reminderTimeISO = result.reminderTime;
        }
        this.cdr.detectChanges();
      }
    });
  }

  formatReminderDisplay(): string {
    if (!this.reminderDate) return '';
    const d = new Date(this.reminderDate);
    return d.toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' })
      + ' ' + d.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
  }

  getReminderDateTime(): string | undefined {
    return this.reminderTimeISO;
  }

  // Status selector - toggle: újra kattintva megszünteti a kijelölést
  selectStatus(statusValue: string): void {
    this.selectedStatus = this.selectedStatus === statusValue ? '' : statusValue;
    this.showReminderSection = this.selectedStatus === 'visszahivando';
    if (!this.showReminderSection) {
      this.reminderDate = null;
      this.reminderTimeISO = undefined;
    }
    this.cdr.detectChanges();
  }

  // Set selected item for menu
  setSelectedItem(item: string, trigger: MatMenuTrigger) {
    this.selectedItem = item;
    this.currentMenuTrigger = trigger;
    trigger.openMenu();
  }

  // Icon menu action handler
  onIconMenuAction(action: SmallIconMenuAction): void {
    switch (action.action) {
      case 'edit':
        this.openEditMenu(this.selectedItem);
        break;
      case 'copy':
        this.copyToClipboard(this.selectedItem, new Event('click'));
        break;
      case 'complete':
        this.markAsCompleted(new Event('click'));
        break;
      case 'reject':
        this.markAsRejected(new Event('click'));
        break;
    }
    
    // Close the menu after action
    if (this.currentMenuTrigger) {
      this.currentMenuTrigger.closeMenu();
    }
  }
  
  // Open edit menu for the selected contact (official, phone, or email)
  openEditMenu(contactValue: string): void {
    if (this.data.officials?.includes(contactValue)) {
      // Tisztségviselő szerkesztése
      this.editingOfficialName = contactValue;
      this.newOfficialName = contactValue;
      this.addOfficialMenuTrigger?.openMenu();
    } else if (this.data.phoneNumbers?.includes(contactValue)) {
      // Telefonszám szerkesztése
      this.editingPhoneValue = contactValue;
      this.editingPhoneInput = contactValue;
      this.addPhoneMenuTrigger?.openMenu();
      // A menü megnyitása után (onMenuOpened lefut először az alap értékkel),
      // majd beállítjuk a szerkesztendő értéket
      setTimeout(() => {
        this.editingPhoneInput = contactValue;
        // Közvetlenül a SmallInputMenuComponent-en is beállítjuk
        if (this.smallInputMenuComponent) {
          this.smallInputMenuComponent.inputControl.setValue(contactValue);
          this.smallInputMenuComponent.inputControl.markAsUntouched();
          this.smallInputMenuComponent.inputControl.markAsPristine();
        }
      }, 50);
    } else if (this.displayEmails?.includes(contactValue)) {
      // Email cím szerkesztése
      this.editingEmailValue = contactValue;
      this.editingEmailInput = contactValue;
      this.addEmailMenuTrigger?.openMenu();
      setTimeout(() => {
        this.editingEmailInput = contactValue;
        if (this.smallEmailInputMenuComponent) {
          this.smallEmailInputMenuComponent.inputControl.setValue(contactValue);
          this.smallEmailInputMenuComponent.inputControl.markAsUntouched();
          this.smallEmailInputMenuComponent.inputControl.markAsPristine();
        }
      }, 50);
    }
  }
  
  // Check if official can be edited
  canEditOfficial(officialName: string): boolean {
    if (!this.data.officialCreatedBy) return false;
    const createdBy = this.data.officialCreatedBy[officialName];
    if (createdBy === null || createdBy === undefined) return false;
    if (this.isAdmin) return true;
    return this.currentUser?.id === createdBy;
  }

  // Copy to clipboard functionality
  copyToClipboard(text: string, event: Event): void {
    event.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      this.closeCurrentMenu();
    }).catch(err => {
      console.error('Failed to copy:', err);
      this.closeCurrentMenu();
    });
  }

  private closeCurrentMenu(): void {
    if (this.currentMenuTrigger) {
      this.currentMenuTrigger.closeMenu();
    }
  }

  // Open add official menu
  openAddOfficialMenu(trigger: MatMenuTrigger): void {
    this.addOfficialMenuTrigger = trigger;
    this.newOfficialName = '';
    trigger.openMenu();
    
    // Focus the input after menu opens
    this.focusAddOfficialInput();
  }

  private focusAddOfficialInput(): void {
    setTimeout(() => {
      const input = document.querySelector('.add-official-menu input') as HTMLInputElement;
      if (input) {
        input.focus();
      }
    }, 100);
  }

  // Cancel adding new official
  cancelAddOfficial(): void {
    this.newOfficialName = '';
    this.editingOfficialName = null; // Szerkesztési mód törlése
    if (this.addOfficialMenuTrigger) {
      this.addOfficialMenuTrigger.closeMenu();
    }
  }

  // Save new official
  saveNewOfficial(): void {
    if (this.newOfficialName?.trim()) {
      const newOfficial = this.newOfficialName.trim();
      
      if (this.editingOfficialName) {
        // Szerkesztési mód: cseréljük le a régi nevet az újra
        if (this.data.officials) {
          const index = this.data.officials.indexOf(this.editingOfficialName);
          if (index !== -1) {
            this.data.officials[index] = newOfficial;
            
            // Átmásoljuk a régi chip státuszát az újra (felhasználói interakció)
            const oldStatus = this.chipStatuses.get(this.editingOfficialName);
            if (oldStatus) {
              this.chipStatuses.delete(this.editingOfficialName);
              this.chipStatuses.set(newOfficial, oldStatus);
            }
            
            // Átmásoljuk a backend validStatus-t is
            if (this.data.officialValidStatuses) {
              const oldValidStatus = this.data.officialValidStatuses[this.editingOfficialName];
              if (oldValidStatus !== undefined) {
                delete this.data.officialValidStatuses[this.editingOfficialName];
                this.data.officialValidStatuses[newOfficial] = oldValidStatus;
              }
            }
            
            // Átmásoljuk a createdby értéket is
            if (this.data.officialCreatedBy) {
              const createdBy = this.data.officialCreatedBy[this.editingOfficialName];
              delete this.data.officialCreatedBy[this.editingOfficialName];
              this.data.officialCreatedBy[newOfficial] = createdBy;
            }
            
            // Csak akkor jegyezzük fel szerkesztésként, ha ez egy létező official volt
            if (this.initialState.officials.includes(this.editingOfficialName)) {
              this.editedOfficials.set(this.editingOfficialName, newOfficial);
            } else {
              // Láncolat szerkesztés: ha egy már szerkesztett official-t szerkesztünk újra
              // (pl. Bob → Charlie → David), frissítjük az eredeti mapping-et (Bob → David)
              for (const [originalName, editedName] of this.editedOfficials) {
                if (editedName === this.editingOfficialName) {
                  this.editedOfficials.set(originalName, newOfficial);
                  break;
                }
              }
            }
          }
        }
      } else {
        // Új hozzáadás mód
        if (this.data.officials) {
          this.data.officials.push(newOfficial);
        } else {
          this.data.officials = [newOfficial];
        }
        
        // Beállítjuk a createdby értéket az aktuális felhasználóra,
        // hogy az edit ikon azonnal megjelenjen az új chipnél
        if (this.currentUser?.id) {
          if (!this.data.officialCreatedBy) {
            this.data.officialCreatedBy = {};
          }
          this.data.officialCreatedBy[newOfficial] = this.currentUser.id;
        }
        
        // Set new contact as confirmed (green) by default
        this.chipStatuses.set(newOfficial, 'confirmed');
      }
      
      this.cancelAddOfficial();
    }
  }

  // Chip action methods
  markAsCompleted(event: Event): void {
    event.stopPropagation();
    
    const currentEffectiveStatus = this.getCurrentEffectiveStatus(this.selectedItem);
    const newStatus: ChipStatus = currentEffectiveStatus === 'confirmed' ? 'default' : 'confirmed';
    
    this.chipStatuses.set(this.selectedItem, newStatus);
    this.closeCurrentMenu();
    this.cdr.detectChanges();
  }

  markAsRejected(event: Event): void {
    event.stopPropagation();
    
    const currentEffectiveStatus = this.getCurrentEffectiveStatus(this.selectedItem);
    const newStatus: ChipStatus = currentEffectiveStatus === 'rejected' ? 'default' : 'rejected';
    
    this.chipStatuses.set(this.selectedItem, newStatus);
    this.closeCurrentMenu();
    this.cdr.detectChanges();
  }

  // Validation methods
  // Új telefon hozzáadása vagy szerkesztése - callback a komponensből
  onPhoneAdded(phoneValue: string) {
    if (!this.data.phoneNumbers) {
      this.data.phoneNumbers = [];
    }
    
    if (this.editingPhoneValue) {
      // Szerkesztési mód: cseréljük le a régi telefonszámot az újra
      const index = this.data.phoneNumbers.indexOf(this.editingPhoneValue);
      if (index !== -1) {
        this.data.phoneNumbers[index] = phoneValue;
        
        // Átmásoljuk a régi chip státuszát az újra (felhasználói interakció)
        const oldStatus = this.chipStatuses.get(this.editingPhoneValue);
        if (oldStatus) {
          this.chipStatuses.delete(this.editingPhoneValue);
          this.chipStatuses.set(phoneValue, oldStatus);
        }
        
        // Átmásoljuk a backend validStatus-t is
        if (this.data.phoneValidStatuses) {
          const oldValidStatus = this.data.phoneValidStatuses[this.editingPhoneValue];
          if (oldValidStatus !== undefined) {
            delete this.data.phoneValidStatuses[this.editingPhoneValue];
            this.data.phoneValidStatuses[phoneValue] = oldValidStatus;
          }
        }
        
        // Átmásoljuk a createdby értéket is
        if (this.data.phoneCreatedBy) {
          const createdBy = this.data.phoneCreatedBy[this.editingPhoneValue];
          delete this.data.phoneCreatedBy[this.editingPhoneValue];
          this.data.phoneCreatedBy[phoneValue] = createdBy;
        }
        
        // Csak akkor jegyezzük fel szerkesztésként, ha ez egy létező telefonszám volt
        if (this.initialState.phones.includes(this.editingPhoneValue)) {
          this.editedPhones.set(this.editingPhoneValue, phoneValue);
        } else {
          // Láncolat szerkesztés: ha egy már szerkesztett telefonszámot szerkesztünk újra
          for (const [originalValue, editedValue] of this.editedPhones) {
            if (editedValue === this.editingPhoneValue) {
              this.editedPhones.set(originalValue, phoneValue);
              break;
            }
          }
        }
      }
      this.editingPhoneValue = null;
    } else {
      // Új hozzáadás mód
      if (!this.data.phoneNumbers.includes(phoneValue)) {
        this.data.phoneNumbers.push(phoneValue);
      }
      
      // Beállítjuk a createdby értéket az aktuális felhasználóra
      if (this.currentUser?.id) {
        if (!this.data.phoneCreatedBy) {
          this.data.phoneCreatedBy = {};
        }
        this.data.phoneCreatedBy[phoneValue] = this.currentUser.id;
      }
      
      // Set new contact as confirmed (green) by default
      this.chipStatuses.set(phoneValue, 'confirmed');
    }
    
    // Force change detection
    this.cdr.detectChanges();
  }

  // Telefon hozzáadás megszakítása - callback a komponensből
  onPhoneAddCancelled(): void {
    this.editingPhoneValue = null; // Szerkesztési mód törlése
  }

  // Új email hozzáadása vagy szerkesztése - callback a komponensből
  onEmailAdded(emailValue: string): void {
    if (this.editingEmailValue) {
      // Szerkesztési mód: cseréljük le a régi email-t az újra
      const index = this.displayEmails.indexOf(this.editingEmailValue);
      if (index !== -1) {
        this.displayEmails[index] = emailValue;
        // Új array referencia a change detection-höz
        this.displayEmails = [...this.displayEmails];
        
        // Átmásoljuk a régi chip státuszát az újra (felhasználói interakció)
        const oldStatus = this.chipStatuses.get(this.editingEmailValue);
        if (oldStatus) {
          this.chipStatuses.delete(this.editingEmailValue);
          this.chipStatuses.set(emailValue, oldStatus);
        }
        
        // Átmásoljuk a backend validStatus-t is
        if (this.data.emailValidStatuses) {
          const oldValidStatus = this.data.emailValidStatuses[this.editingEmailValue];
          if (oldValidStatus !== undefined) {
            delete this.data.emailValidStatuses[this.editingEmailValue];
            this.data.emailValidStatuses[emailValue] = oldValidStatus;
          }
        }
        
        // Átmásoljuk a createdby értéket is
        if (this.data.emailCreatedBy) {
          const createdBy = this.data.emailCreatedBy[this.editingEmailValue];
          delete this.data.emailCreatedBy[this.editingEmailValue];
          this.data.emailCreatedBy[emailValue] = createdBy;
        }
        
        // Csak akkor jegyezzük fel szerkesztésként, ha ez egy létező email volt
        if (this.initialState.emails.includes(this.editingEmailValue)) {
          this.editedEmails.set(this.editingEmailValue, emailValue);
        } else {
          // Láncolat szerkesztés: ha egy már szerkesztett email-t szerkesztünk újra
          for (const [originalValue, editedValue] of this.editedEmails) {
            if (editedValue === this.editingEmailValue) {
              this.editedEmails.set(originalValue, emailValue);
              break;
            }
          }
        }
      }
      this.editingEmailValue = null;
    } else {
      // Új hozzáadás mód
      if (!this.displayEmails.includes(emailValue)) {
        this.displayEmails = [...this.displayEmails, emailValue];
      }
      
      // Beállítjuk a createdby értéket az aktuális felhasználóra
      if (this.currentUser?.id) {
        if (!this.data.emailCreatedBy) {
          this.data.emailCreatedBy = {};
        }
        this.data.emailCreatedBy[emailValue] = this.currentUser.id;
      }
      
      // Set new contact as confirmed (green) by default
      this.chipStatuses.set(emailValue, 'confirmed');
    }
    
    // Force change detection
    this.cdr.detectChanges();
  }

  // Email hozzáadás megszakítása - callback a komponensből
  onEmailAddCancelled(): void {
    this.editingEmailValue = null; // Szerkesztési mód törlése
  }

  // Getter: NINCS KONVERZIÓ! Ugyanaz az AdminInteraction[] mint az admin oldalon!
  get convertedLogs(): AdminInteraction[] {
    if (!this.data.logs) return [];
    return this.data.logs;
  }
}
